import { useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ITeacherAttendanceStudent } from '@/entities/TeacherAttendance';
import { AppTable } from '@/shared/ui/AppTable';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { AttendanceSelect } from '../AttendanceSelect';

export type StudentWithAttendance = ITeacherAttendanceStudent & {
  selectedAttendance: 0 | 0.5 | 1 | null;
};

interface LessonAttendanceStudentsTableProps {
  students: StudentWithAttendance[];
  onAttendanceChange: (studentId: number, value: 0 | 0.5 | 1) => void;
  isReadOnly?: boolean;
}

export const LessonAttendanceStudentsTable = ({
  students,
  onAttendanceChange,
  isReadOnly = false,
}: LessonAttendanceStudentsTableProps) => {
  const handleAttendanceChange = useCallback(
    (studentId: number, value: string) => {
      const attendanceValue = value === '0' ? 0 : value === '0.5' ? 0.5 : value === '1' ? 1 : null;
      if (attendanceValue !== null) {
        onAttendanceChange(studentId, attendanceValue);
      }
    },
    [onAttendanceChange],
  );

  const columns = useMemo<ColumnDef<StudentWithAttendance>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }) => <span className="text-black">{row.original.id}</span>,
      },
      {
        accessorKey: 'username',
        header: 'User',
        cell: ({ row }) => {
          const student = row.original;
          return (
            <UserAvatar
              avatar={student.avatar}
              userName={student.username}
              className="flex items-center gap-x-2"
            />
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const student = row.original;
          const attendanceValue = student.selectedAttendance;

          return (
            <AttendanceSelect
              name={`attendance-${student.id}`}
              value={attendanceValue !== null ? String(attendanceValue) : ''}
              onChange={(value: string) => handleAttendanceChange(student.id, value)}
              placeholder="Select"
              disabled={isReadOnly}
            />
          );
        },
      },
    ],
    [handleAttendanceChange, isReadOnly],
  );

  return (
    <AppTable
      caption="Attendance for this lesson"
      data={students}
      columns={columns}
      getSelectedItems={() => {}}
    />
  );
};
